import { z } from 'zod';
import { baseListQuery, sortOrderSchema, uuidSchema } from './common';
import type { ModuleMailOutcome, ServiceMailTargets } from './module-mail';
import { createOfficeEquipmentSchema } from './office-equipment';
import type { OfficeEquipmentObjectRefDto, OfficeEquipmentTypeRefDto } from './office-equipment';
import type { ServiceRequestStatus } from './service-requests';

// ── Кандидат на добавление техники (план `docs/office-equipment-candidate-plan.md`: Р1–Р7, Р12–Р15;
// ADR 0085 — модуль, ADR 0146 — заявка без аппарата) ──
//
// СООБЩЕНИЕ О ТЕХНИКЕ, А НЕ ЗАПИСЬ СПРАВОЧНИКА, и это главное, что надо знать про этот файл.
// Заявитель, у которого нет `officeEquipment.write`, сегодня упирается в тупик: аппарата в
// справочнике нет, завести его нечем, и заявку человек в этот момент бросает. Кандидат — его
// свидетельство «в кабинете 214 стоит вот это», которое проходит проверку и только решением
// человека с `write` превращается в карточку парка.
//
// ПОЧЕМУ СВОЯ СУЩНОСТЬ, А НЕ НЕАКТИВНАЯ КАРТОЧКА `office_equipment` (Р1). У кандидата другая
// область видимости и другие правила жизни, а вписанный в таблицу парка он оплачивался бы
// исключением в КАЖДОМ её читателе: список, карточка, селектор формы заявки, обмен файлом,
// счётчики моделей и расходников, гарантии, история. Требование «до подтверждения запись не
// попадает никуда» в том варианте выполняется списком, который невозможно закрыть, — он открыт для
// каждой будущей ручки парка; здесь оно выполняется тем, что записи в парке ещё нет.
//
// ЧЕГО У КАНДИДАТА НЕТ и чего в этом файле искать не надо: отдела-владельца, даты покупки,
// гарантии, признака активности и ссылки на модель справочника (Р7). Это реквизиты УЧЁТА, а не
// наблюдения: заявитель их не знает и, спросив их, форма получила бы выдуманные ответы. Их
// проставляет проверяющий в форме подтверждения — полной форме карточки парка (Р13, В7).

/**
 * Четыре состояния (Р3): `pending` — сообщение ждёт проверки; дальше ровно одно из трёх решений
 * проверяющего — `confirmed` (по сообщению заведена новая карточка), `duplicate` (аппарат уже был в
 * справочнике, кандидат объединён с существующей карточкой), `rejected` (аппарата в названном месте
 * нет либо сообщение недостоверно).
 *
 * ВСЕ ТРИ ИСХОДА КОНЕЧНЫ: правка после решения не предусмотрена вовсе (Р15), «передумал»
 * оформляется новой заявкой с новым кандидатом. Отсюда и то, что частичные уникальные индексы
 * ожидающих (Р3) сторожат только `pending`: после решения номер освобождается, и новый факт можно
 * сообщить новой парой «кандидат + заявка».
 *
 * ПЕРЕЧНЕМ В КОНТРАКТАХ, А НЕ ЧЕТЫРЬМЯ СТРОКАМИ ПО МЕСТУ: те же слова стоят в `CHECK` базы
 * (`office_equipment_candidates_status_check`) и в `$type` колонки схемы, и второй список разошёлся
 * бы с ними молча.
 */
export const OFFICE_EQUIPMENT_CANDIDATE_STATUSES = [
  'pending',
  'confirmed',
  'duplicate',
  'rejected',
] as const;
export type OfficeEquipmentCandidateStatus = (typeof OFFICE_EQUIPMENT_CANDIDATE_STATUSES)[number];

/**
 * Подписи называют ИСХОД, а не состояние машины, и адресат у них один — заявитель, который читает
 * их в своей заявке. «Подтверждён» и «Дубль» верны технически и не отвечают на его единственный
 * вопрос: появился аппарат в справочнике или нет. `duplicate` при этом не «вы прислали дубль»:
 * человек честно не нашёл карточку — она числилась за чужой площадкой или была снята с
 * эксплуатации, — и подпись не должна читаться упрёком.
 */
export const officeEquipmentCandidateStatusLabels: Record<OfficeEquipmentCandidateStatus, string> =
  {
    pending: 'На проверке',
    confirmed: 'Заведён в справочник',
    duplicate: 'Аппарат уже был в справочнике',
    rejected: 'Отклонён',
  };

// ── Шесть заявленных реквизитов (Р7) ──
//
// Пределы длин те же, что у одноимённых полей карточки парка, и это не совпадение: подтверждение
// переносит заявленное в форму карточки один в один, и поле, которое здесь длиннее, там пришлось бы
// обрезать молча. Своими константами, а не общими с `office-equipment.ts`: там они приватны, а
// главное — смысл у полей разный. `declaredModel` — это НЕ наименование карточки: карточка с
// выпуска A носит имя-зеркало своей модели справочника, а здесь лежит то, что человек прочёл с
// шильдика, вместе с опечатками и «кажется, Kyocera».

/** «Kyocera ECOSYS M3145» с шильдика. Обязательна: без модели проверяющему не с чем идти к парку. */
const declaredModelSchema = z.string().trim().min(2, 'Укажите модель с шильдика').max(255);

/** Номера свободного вида: их печатает производитель и клеит бухгалтерия, формата у них нет. */
const candidateNumberSchema = z.string().trim().max(100);

/**
 * «каб. 214» — единственное, по чему аппарат находят физически, поэтому обязательно (в отличие от
 * карточки парка, где место бывает пустым: там единицу уже нашли и завели). Тот же инвариант держит
 * `office_equipment_candidates_location_not_blank_check`.
 */
const candidateLocationSchema = z.string().trim().min(1, 'Укажите место: «каб. 214»').max(255);

/** «стоит у бухгалтерии, наклейки нет» — необязательное уточнение к пяти реквизитам выше. */
const candidateCommentSchema = z.string().trim().max(2000);

/**
 * Хотя бы один номер обязателен — тот же инвариант, что у парка, и тот же, что держит
 * `office_equipment_candidates_identity_check`. Не формальность: по номеру ищется дубль на всех
 * трёх рубежах (Р10), и кандидат без обоих номеров нечем ни сверить с парком, ни отличить от
 * соседнего такого же МФУ.
 */
const IDENTITY_MESSAGE = 'Укажите серийный или инвентарный номер с шильдика';

/**
 * Поля россыпью, а не готовой схемой, ровно по той же причине, что `equipmentFields` у карточки
 * парка: правка проверяющего (Р12) — это те же шесть реквизитов плюс версия, а схему с `.refine`
 * расширить нечем. Собрав правку из отдельного списка полей, мы бы завели второе место, где
 * записаны пределы длин, и разошлись бы с ним на первой же правке.
 */
const candidateFields = {
  /**
   * Тип — ссылкой на обычный справочник типов (перечень из десятка строк): без него проверяющий не
   * знает, какую форму открывать, а очередь не разделить на «МФУ» и «ноутбуки». Заводить типы
   * заявителю при этом нельзя — он их только выбирает.
   */
  equipmentTypeId: uuidSchema,
  /**
   * Модель ТЕКСТОМ, и это решение, а не упрощение формы (Р7). Справочник моделей — такая же запись
   * справочника, и открыв его заявителю, мы вернули бы отобранный `write` боковой дверью: перечень
   * моделей пополнялся бы теми, кто карточки вести не должен. Ссылку на модель проставляет
   * проверяющий в форме подтверждения.
   */
  declaredModel: declaredModelSchema,
  serialNumber: candidateNumberSchema.optional().default(''),
  inventoryNumber: candidateNumberSchema.optional().default(''),
  /**
   * Физическое место аппарата. Проверяется сервером по оси роли автора (объектная — из
   * `constructionObjectIds`, отдельская — из `departmentObjectIds`, сквозная — из справочника);
   * чужой объект — 422. Заказчиком заявки этот объект при этом не становится: у сотрудника отдела
   * заказчиком остаётся его отдел, иначе он потерял бы собственную заявку по правилу ADR 0146.
   */
  objectId: uuidSchema,
  location: candidateLocationSchema,
  comment: candidateCommentSchema.optional().default(''),
};

const hasAnyNumber = (v: { serialNumber: string; inventoryNumber: string }): boolean =>
  !!v.serialNumber || !!v.inventoryNumber;

/**
 * Кандидат, каким его присылает заявитель. Отдельной ручки «завести кандидата» нет вовсе (Р2): эта
 * схема встраивается в `createServiceRequestSchema` полем `equipmentCandidate` ВМЕСТО
 * `officeEquipmentId`, и обе строки — кандидат и заявка — появляются одной транзакцией. Отдельный
 * вход оставлял бы кандидатов-сирот при каждом обрыве на втором шаге и требовал бы уборки.
 *
 * Ни автора, ни ключа идемпотентности, ни версии здесь нет: автора и его подразделение проставляет
 * сервер из учётки (иначе кандидата можно было бы подать от чужого имени), ключ приходит
 * заголовком `Idempotency-Key` — тем же транспортом, что у закупки и кабинета водителя, — а версия
 * у только что созданной строки равна единице по определению.
 *
 * Фото шильдика форма ПРОСИТ, но не требует (В4): снимок снимает половину работы проверяющего, но
 * аппарат стоит в коридоре, телефон бывает без камеры, а запертая на вложении заявка означает
 * несделанную заявку. Механики новой ему не нужно — вложение приезжает обычным `fileIds` заявки, и
 * в этой схеме его поэтому нет.
 */
export const equipmentCandidateInputSchema = z.object(candidateFields).refine(hasAnyNumber, {
  message: IDENTITY_MESSAGE,
  path: ['inventoryNumber'],
});
export type EquipmentCandidateInput = z.infer<typeof equipmentCandidateInputSchema>;

/**
 * Версия содержимого кандидата: её несут правка и все три решения (Р11, Р12).
 *
 * ОБЯЗАТЕЛЬНА ВЕЗДЕ, И ЭТО НЕ ОСТОРОЖНОСТЬ. Статуса для защиты не хватает: правка идёт ВНУТРИ
 * `pending`, где статус неподвижен и ничего не стережёт, — двое проверяющих, открывших одну строку
 * очереди, перетёрли бы исправления друг друга молча. У решений своя половина той же беды: «Отклонить»
 * и «Подтвердить», нажатые одновременно, оставили бы в базе подтверждённого кандидата с причиной
 * отказа. Условная запись `WHERE status = 'pending' AND content_version = ?` с обязательным
 * `ROW_COUNT = 1` отвечает на оба вопроса сразу, а ноль строк — это 409 со свежим состоянием.
 *
 * Считается с единицы: отправленный кандидат — это уже первая редакция свидетельства.
 */
const expectedVersionSchema = z.number().int().min(1);

/**
 * Правка реквизитов проверяющим до решения (Р12): «заявитель списал `O` вместо `0`».
 *
 * ПРАВИТ ТОЛЬКО ПРОВЕРЯЮЩИЙ, И ТОЛЬКО ПОКА `pending`. Автору правка закрыта после отправки
 * намеренно: кандидат — свидетельство о том, что человек видел в кабинете, и переписанное задним
 * числом свидетельство ничего не доказывает; уточнения идут репликой в обсуждении заявки
 * (ADR 0141) — канал уже есть, и второго заводить незачем. Отклонять же заявку из-за одной опечатки
 * значило бы заставить человека заводить её заново.
 *
 * Поля присылаются ПОЛНЫМ набором, а не разницей: их шесть, форма показывает все шесть сразу, и
 * «стереть комментарий» с «не трогать комментарий» частичным телом не различить.
 */
export const updateOfficeEquipmentCandidateSchema = z
  .object({ expectedVersion: expectedVersionSchema, ...candidateFields })
  .refine(hasAnyNumber, { message: IDENTITY_MESSAGE, path: ['inventoryNumber'] });
export type UpdateOfficeEquipmentCandidateInput = z.infer<
  typeof updateOfficeEquipmentCandidateSchema
>;

/**
 * "Confirm the equipment" (Р13, В7): the version and the FULL FLEET CARD FORM, prefilled with what
 * was declared.
 *
 * NOT A "CREATE AS IS" BUTTON, and that is a survey decision. The reviewer does not agree with the
 * report but creates a card FROM it: the requester does not know half of the accounting details
 * (Р7), and a card made from their words one to one would be incomplete from day one — without a
 * model reference, an owner department or a warranty, that is invisible to the fleet counters and
 * to the "Warranties" tab. Since release B of ADR 0132 the model reference is required here as on
 * the first door: the declared model text stays evidence and never becomes the card's input.
 *
 * The form is the same `createOfficeEquipmentSchema` nested, not a rewritten field list: the second
 * door into the fleet must accept exactly what the first one does (Р14), and diverging on one
 * optional field they would produce cards of two kinds. Nested, because the card schema carries a
 * `.refine` ("at least one number") and cannot be extended, and a copy of its rules next to it
 * would drift from the original on the first edit.
 *
 * `lazy` instead of a direct reference because of an import cycle, and not as a precaution (Э2).
 * Since `equipmentCandidateInputSchema` was embedded into `createServiceRequestSchema` (Р2), three
 * files close a ring: the request takes the candidate schema, the candidate takes the card form from
 * the directory, and the directory takes `projectByAudiencePolicy` from the request. The ring is
 * legal by itself; only reading another module's VALUE while modules evaluate breaks it: entry via
 * `index.ts` starts with the directory, and a direct reference would fail here with "Cannot access
 * 'createOfficeEquipmentSchema' before initialization" — on loading the whole package, not in a
 * rare case. `lazy` defers the access to the first parse, when all three modules are evaluated;
 * verified by running it.
 */
export const confirmOfficeEquipmentCandidateSchema = z.object({
  expectedVersion: expectedVersionSchema,
  equipment: z.lazy(() => createOfficeEquipmentSchema),
});
export type ConfirmOfficeEquipmentCandidateInput = z.infer<
  typeof confirmOfficeEquipmentCandidateSchema
>;

/**
 * «Это уже заведённый аппарат» (Р15): исход `duplicate`. Карточку завели руками, пока сообщение
 * стояло в очереди, либо заявитель не нашёл её из-за области видимости.
 *
 * Целевую карточку сервер проверяет по трём условиям сразу — область проверяющего, `deleted_at IS
 * NULL` и `is_active = true`: снятую с эксплуатации сначала возвращают в работу отдельным явным
 * действием, иначе объединение обошло бы серверный запрет заводить заявки на неактивную технику.
 */
export const mergeOfficeEquipmentCandidateSchema = z.object({
  expectedVersion: expectedVersionSchema,
  officeEquipmentId: uuidSchema,
});
export type MergeOfficeEquipmentCandidateInput = z.infer<
  typeof mergeOfficeEquipmentCandidateSchema
>;

/**
 * «Отклонить» (Р15): исход `rejected` — аппарата в названном месте нет либо сообщение
 * недостоверно.
 *
 * ПРИЧИНА ОБЯЗАТЕЛЬНА И УХОДИТ ЗАЯВИТЕЛЮ ДОСЛОВНО (В5) — и в карточку заявки, и в письмо. Общая
 * формулировка «сообщение отклонено, подробности в портале» экономила бы проверяющему выбор слов
 * ценой второго действия человека: он всё равно пойдёт смотреть причину, а не поняв её, позвонит в
 * ИТ-службу — то есть ровно тем звонком, ради отмены которого модуль и заводился. Плата названа:
 * причину читает не только проверяющий, и форма отказа предупреждает об этом подсказкой.
 *
 * То же требует и база (`office_equipment_candidates_reason_check` меряет причину через `btrim`):
 * отказ без объяснения через месяц читается как «передумали».
 */
export const rejectOfficeEquipmentCandidateSchema = z.object({
  expectedVersion: expectedVersionSchema,
  reason: z.string().trim().min(3, 'Укажите причину отказа').max(1000),
});
export type RejectOfficeEquipmentCandidateInput = z.infer<
  typeof rejectOfficeEquipmentCandidateSchema
>;

/**
 * Поля сортировки очереди проверки. Оно одно, и это не заготовка под будущее: у сообщения нет ни
 * номера, ни суммы, ни срока — по порядку его строки отличаются друг от друга только возрастом.
 * «Кто раньше сообщил» и есть очередь.
 */
export const OFFICE_EQUIPMENT_CANDIDATE_SORT_FIELDS = ['createdAt'] as const;

/**
 * Рабочая очередь проверяющего (§8): страница и отбор по состоянию.
 *
 * СТАРЫЕ СВЕРХУ ПО УМОЛЧАНИЮ, и это единственный список модуля, у которого `sortOrder` начинается
 * с `asc`. У остальных списков вопрос «что нового», и свежее сверху на него отвечает; здесь вопрос
 * «чья очередь», и свежее сверху означало бы, что сообщение, до которого не дошли руки в первый
 * день, не дождётся проверки никогда — очередь бы работала как стек. Тот же порядок описан и
 * частичным индексом `office_equipment_candidates_pending_idx`.
 *
 * ПОИСКА НЕТ ВОВСЕ, и это не экономия. Очередь ожидающих — десяток строк, которые проверяющий
 * разбирает целиком, а не ищет в них; поиск по номеру обещал бы ответ на вопрос «есть ли такой
 * аппарат», а его задают ПАРКУ, и отвечает на него рубеж 1 при заведении (Р10). Найденное здесь
 * означало бы ровно обратное — «в парке этого нет, об этом только сообщили».
 *
 * Отбор по состоянию — тем же приёмом «через запятую в одном параметре», что у закупки
 * (`officeEquipmentPurchaseListQuerySchema`) и у журнала аудита: повтор параметра разбирается
 * по-разному в зависимости от того, как собран запрос, а строка с запятой одна при любом клиенте.
 * Отсутствие параметра означает «все состояния», пустого набора не бывает: рабочий срез
 * «ожидающие» портал присылает явно, а «покажи ничего» — это не отбор, а ошибка формы.
 */
export const officeEquipmentCandidateListQuerySchema = baseListQuery(
  OFFICE_EQUIPMENT_CANDIDATE_SORT_FIELDS,
)
  .omit({ search: true })
  .extend({
    sortOrder: sortOrderSchema.default('asc'),
    status: z
      .string()
      .max(OFFICE_EQUIPMENT_CANDIDATE_STATUSES.join(',').length)
      .transform((v) =>
        v
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s !== ''),
      )
      .pipe(
        z
          .array(z.enum(OFFICE_EQUIPMENT_CANDIDATE_STATUSES))
          .nonempty('Укажите хотя бы одно состояние или не присылайте отбор вовсе')
          .max(OFFICE_EQUIPMENT_CANDIDATE_STATUSES.length),
      )
      .optional(),
  });
export type OfficeEquipmentCandidateListQuery = z.infer<
  typeof officeEquipmentCandidateListQuerySchema
>;

/**
 * Чем объясняется 409 при заведении пары «кандидат + заявка» (Р10, §8).
 *
 * СВОИМИ КОДАМИ, А НЕ ОБЩИМ `version_conflict`, потому что исходов у этих трёх отказов три разных,
 * и портал обязан их различать: дубль в парке предлагает выбрать найденную единицу (и получает её
 * идентификатор), ожидающий дубль означает «сообщение уже в очереди — ждите», а занятый ключ
 * означает, что попытка отправки переиспользована под изменённое тело, и повторять её нечем.
 * Общий код свёл бы все три к одному «обновите данные и повторите» — совету, который не помогает ни
 * в одном из них.
 */
export const OFFICE_EQUIPMENT_CANDIDATE_CONFLICT_CODES = {
  /** Рубеж 1: живая карточка с таким номером уже есть в парке. */
  parkDuplicate: 'office_equipment_candidate_park_duplicate',
  /** Рубеж 2: об этом аппарате уже сообщили, и сообщение ждёт проверки. */
  pendingDuplicate: 'office_equipment_candidate_pending_duplicate',
  /** Тот же `Idempotency-Key` уже занят ДРУГОЙ командой. */
  idempotency: 'office_equipment_candidate_idempotency',
} as const;

/**
 * Тело отказа рубежа 1: то, чем портал продолжает заявку без второго действия человека.
 *
 * ИДЕНТИФИКАТОР ЗАПОЛНЕН РОВНО У ОДНОЙ ИЗ ТРЁХ РЕДАКЦИЙ — у активной карточки в области заявителя
 * (Р10). У неактивной он пуст намеренно: активный селектор её не показывает, а сервер всё равно
 * отобьёт заявку на выведенную из эксплуатации карточку (Ф2), — подставленный идентификатор обещал
 * бы ход, которого нет. У чужой пусто и наименование: раскрывается только факт «такой номер
 * существует», а его человек и так знает — он сам его и ввёл.
 */
export interface OfficeEquipmentCandidateParkDuplicateDetails {
  kind: 'parkDuplicate';
  officeEquipmentId: string | null;
  /** `officeEquipmentTitle` найденной карточки — та же подпись, что в списке и в письме. */
  title: string | null;
}

/**
 * Автор сообщения — ТОЛЬКО ПРОВЕРЯЮЩЕМУ (Р9). Остальным блок не отдаётся вовсе: контакт заявителя
 * уже виден в самой заявке, и второй раз перечислять его рядом с предметом значило бы расширять
 * видимость персональных данных ради удобства вёрстки.
 *
 * Отсутствие поля в DTO означает «этот ответ такого среза не содержит», а не «автор неизвестен», —
 * тот же приём, что у среза расходников в карточке парка.
 */
export interface OfficeEquipmentCandidateAuthorDto {
  id: string;
  name: string;
  /**
   * Подразделение автора — СНИМОК на момент отправки, а не сегодняшняя привязка учётки: по нему
   * считается отдельская ось области проверяющего (Р9), и переведённый в другой отдел человек не
   * должен уносить свои прошлые сообщения из чужой очереди. Пусто у учёток вовсе без привязок
   * (администратор) — законное состояние, а не потерянные данные.
   */
  departmentName: string | null;
}

/**
 * Чем кончилась проверка: карточка парка, которую завели по сообщению (`confirmed`) либо с которой
 * кандидата объединили (`duplicate`).
 *
 * Подписью, а не полным DTO карточки: смотрящему нужно узнать аппарат («Kyocera M3145 · инв.
 * 0012345») и открыть его по ссылке, а реквизиты он прочтёт в самой карточке — с её собственной
 * проверкой области, а не с областью кандидата.
 */
export interface OfficeEquipmentCandidateResultDto {
  id: string;
  /** `officeEquipmentTitle` карточки: одна и та же подпись в портале, в письме и в отказе. */
  title: string;
}

/**
 * Единственная связанная заявка (Р4, пара 1:1). Она есть у кандидата ВСЕГДА — кандидатов без заявки
 * не бывает по построению, — поэтому `null` здесь означает «смотрящему эта заявка не видна», а не
 * «заявки нет».
 *
 * Статус в ссылке нужен затем, что решение по технике и ход заявки — разные события (Р16): заявку
 * могли отменить или закрыть, пока сообщение стояло в очереди, и проверяющий обязан видеть это до
 * того, как заведёт карточку.
 */
export interface OfficeEquipmentCandidateRequestRefDto {
  id: string;
  num: number;
  /** «СО-14» — собранный сервером, чтобы номер в очереди, в письме и в заявке был один. */
  displayNumber: string;
  status: ServiceRequestStatus;
}

/**
 * Кандидат целиком. Приезжает двумя путями: строкой очереди проверки (`review`) и встроенным блоком
 * в карточке связанной заявки — там его видят автор и все, кому видна сама заявка (Р9).
 *
 * ЛЕНТА ЖИЗНИ КАНДИДАТА СТРОИТСЯ ИЗ ЭТИХ КОЛОНОК, А НЕ ИЗ ЖУРНАЛА АУДИТА — довод закупки: общий
 * помощник `writeAudit` глотает ошибку записи, то есть гарантией истории не является, и карточка,
 * показывающая путь сообщения из такого источника, показывала бы его с дырами. Три пары «кто и
 * когда» пишутся той же транзакцией, что и статус, и лента полна по построению.
 */
export interface OfficeEquipmentCandidateDto {
  id: string;
  status: OfficeEquipmentCandidateStatus;
  /** Версия содержимого: её возвращает форма проверки и несут все три кнопки решения (Р11). */
  contentVersion: number;
  equipmentType: OfficeEquipmentTypeRefDto;
  /** Что человек прочёл с шильдика. Ссылкой на справочник моделей не становится никогда (Р7). */
  declaredModel: string;
  serialNumber: string;
  inventoryNumber: string;
  object: OfficeEquipmentObjectRefDto;
  location: string;
  comment: string;
  /** Только проверяющему (Р9); отсутствие поля — «срез не отдан», а не «автора нет». */
  author?: OfficeEquipmentCandidateAuthorDto;
  createdAt: string;
  /**
   * Кто и когда правил реквизиты до решения (Р12). Пусто у нетронутого кандидата — это и есть
   * ответ «сообщение приняли как есть», а не потерянная запись.
   */
  updatedByName: string | null;
  updatedAt: string;
  decidedByName: string | null;
  decidedAt: string | null;
  /**
   * Причина решения. Пустая строка у `pending` и у обоих положительных исходов: причина
   * обязательна ровно у отказа (`…_reason_check`), а «почему завели карточку» отвечает сама
   * карточка.
   */
  decisionReason: string;
  /** Заполнен ровно у `confirmed` и `duplicate` — это же держит `…_result_check`. */
  resultEquipment: OfficeEquipmentCandidateResultDto | null;
  /** `null` — заявка есть, но смотрящему не видна (см. `OfficeEquipmentCandidateRequestRefDto`). */
  request: OfficeEquipmentCandidateRequestRefDto | null;
}

/**
 * Отказ приёмки, пока сообщение о технике ещё на проверке (Р16, §8 последний абзац).
 *
 * В КОНТРАКТАХ, А НЕ ПО КОПИИ НА КАЖДОЙ СТОРОНЕ. Текст говорят двое: сервер — отказом
 * `PATCH /:id/accept` после блокировки заявки и кандидата, портал — причиной выключенного пункта
 * «Принять работу» в меню заявки. Слова у них обязаны совпадать дословно: человек читает причину в
 * меню, жмёт всё равно (быстрая смена статуса переносит `disabled` на тег, но путей к приёмке два)
 * и получает второй, другой по формулировке отказ — и решает, что это две разные поломки. Своя
 * строка на каждой стороне разошлась бы на первой же правке формулировки, и разошлась бы молча.
 *
 * Замок стоит ровно у `pending`. У `rejected` его нет намеренно (Р16): проверка закончена, и
 * заявка, по которой работу уже выполнили, не должна застревать в «Решена» из-за справочника.
 */
export const CANDIDATE_PENDING_ACCEPT_REFUSAL = 'Сначала дождитесь решения по технике';

/**
 * Ответ ТРЁХ РУЧЕК РЕШЕНИЯ (план кандидатов, §10). Тот же кандидат плюс исход почтовой части —
 * аддитивно, двумя необязательными полями, а не новой обёрткой `{ candidate, mail }`.
 *
 * ФОРМА ОТВЕТА НЕ МЕНЯЕТСЯ НАМЕРЕННО. Обёртка была бы честнее по форме и хуже по цене: очередь и
 * карточка читают тот же DTO, портал волны кандидатов пишется прямо сейчас, и смена корня ответа
 * означала бы правку в чужих руках ради двух полей, которые старый клиент просто не заметит.
 * Необязательность здесь — не «может не прийти когда как»: поля стоят у всех трёх решений и
 * отсутствуют ровно там, где кандидат приезжает НЕ решением (очередь, карточка, встроенный блок
 * заявки), — то есть отсутствие поля означает «этот ответ про почту ничего не утверждает».
 *
 * `mail` — общий исход по ОБЯЗАТЕЛЬНОЙ цели письма (автор сообщения), `mailTargets` — разбор по
 * целям: копия, ушедшая наблюдателю, не отменяет того, что сам заявитель о решении не узнал.
 * Правило то же, что у заявок (`ServiceMailTargets`), и второго правила здесь быть не должно.
 */
export interface OfficeEquipmentCandidateDecisionDto extends OfficeEquipmentCandidateDto {
  mail?: ModuleMailOutcome;
  mailTargets?: ServiceMailTargets;
}
